import cx from "classnames";
import * as React from "react";
import { CheckboxStateEnum, QESCheckbox } from "./Checkbox";
import TooltipWrapper from "../../TooltipWrapper";

import { FormFieldStates, InputProps } from "./exports";
import { QESTextInput } from "./TextInput";

export interface MultiselectItem {
  canBeIndeterminate?: boolean;
  currentState: CheckboxStateEnum;
  displayValue: string;
}

export interface MultiselectOnChangeResponse {
  checkedKeys: string[];
  indeterminateKeys: string[];
  options: { [key: string]: MultiselectItem }
}

export interface QESMultiSelectProps extends InputProps {
  /**
   *  Declares whether the field can be interacted with
   */
  disabled?: boolean;
  /**
   * Defines how many checkboxes can be checked
   */
  maxSelected?: number;
  /**
   * An explicit onChange handler that takes the changed item and its new state
   */
  onChange: (response: MultiselectOnChangeResponse) => void;
  /**
   *  The list of options to go in the multiselect as key/value pairs. The key differentiates the items and should be unique, but value doesn't
   *  have to be.
   *
   *  Of course, you're asking for trouble if you render duplicate values, as the user won't be able to tell
   *  one they're selecting.
   *  Empty strings also don't work properly.
   */
  options: { [key: string]: MultiselectItem };
  /**
   *  An optional prefix to prefend checkbox ids, in the event multiple multiselects have checkboxes with the same ids on the same page
   */
  prefix?: string;
}

/**
 * It's not so much a multiselect as a list of checkboxes, but that's because the built-in multiselect sucks.
 */
/* We call out the custom props here individually, so ...props is just the normal HTML input attributes */
export const QESMultiSelect: React.FC<React.PropsWithChildren<QESMultiSelectProps>> = (props) => {
  const [hasBeenFocused, setHasBeenFocused] = React.useState(false);
  const [fieldState, setFieldState] = React.useState(FormFieldStates.None);
  const [filterString, setFilterString] = React.useState("");

  const subText = React.useMemo(() => {
    if (hasBeenFocused && props.errorText && props.isValid !== undefined && fieldState === FormFieldStates.Error) {
      return props.errorText;
    } else if (props.descriptiveText) {
      return props.descriptiveText;
    }
    return "";
  }, [props.descriptiveText, props.errorText, fieldState, hasBeenFocused]);

  const selectedKeys = React.useMemo(() => {
    return Object.entries(props.options).filter(([, value]) => value.currentState === CheckboxStateEnum.Checked).map(([key]) => key);
  }, [props.options]);

  const indeterminateKeys = React.useMemo(() => {
    return Object.entries(props.options).filter(([, value]) => value.currentState === CheckboxStateEnum.Indeterminate).map(([key]) => key);
  }, [props.options]);

  React.useEffect(() => {
    if (!props.isValid && props.required && hasBeenFocused && props.isValid !== undefined) {
      setFieldState(FormFieldStates.Error);
    } else {
      setFieldState(FormFieldStates.None);
    }
  }, [props.isValid, hasBeenFocused, props.required, props.unrequiredValidation, props.wasFocusedExternally]);

  const handleCheckboxClick = (key: string) => {
    setHasBeenFocused(true);

    const option = props.options[key];
    const response: MultiselectOnChangeResponse = {
      checkedKeys: [...selectedKeys],
      indeterminateKeys: [...indeterminateKeys],
      options: { ...props.options },
    };
    // switch on the props value and not the response value so we don't hit multiple cases
    switch (props.options[key].currentState) {
      // if it's unchecked, see if it can be indeterminate
      // if it can, make it indeterminate. otherwise make it checked.
      case CheckboxStateEnum.Unchecked:
        if (option.canBeIndeterminate) {
          response.indeterminateKeys.push(key);
          response.options[key].currentState = CheckboxStateEnum.Indeterminate;
        } else {
          response.checkedKeys.push(key);
          response.options[key].currentState = CheckboxStateEnum.Checked;
        }
        break;
      // if it's indeterminate, make it checked
      case CheckboxStateEnum.Indeterminate:
        response.indeterminateKeys.splice(response.indeterminateKeys.findIndex((ik) => ik === key), 1);
        response.checkedKeys.push(key);
        response.options[key].currentState = CheckboxStateEnum.Checked;
        break;
      // if it's checked, make it unchecked
      case CheckboxStateEnum.Checked:
        response.checkedKeys.splice(response.checkedKeys.findIndex((ik) => ik === key), 1);
        response.options[key].currentState = CheckboxStateEnum.Unchecked;
        break;
    }

    props.onChange(response);
  }

  const renderedOptions = React.useMemo(() => {
    return Object.entries(props.options)
      .filter(([, value]) => value.displayValue.toLowerCase().includes(filterString.toLowerCase()))
      .map(([key, value]) => {
        const isChecked = !!selectedKeys.find((o) => o === key);
        const isIndeterminate = !!indeterminateKeys.find((o) => o === key);
        return (
          <li className="qes-multiselect__check-item" key={key}>
            <QESCheckbox
              label={value.displayValue}
              testId={key}
              prefix={props.prefix}
              onChange={() => handleCheckboxClick(key)}
              checked={isChecked}
              indeterminate={isIndeterminate}
              disabled={props.disabled || (props.maxSelected !== undefined && selectedKeys.length >= props.maxSelected && !isChecked)}
            />
          </li>
        );
      });
  }, [props.options, filterString, props.disabled]);

  const renderedSelectedOptions = React.useMemo(() => {
    const newOptions = Object.entries(props.options).filter(([, value]) => {
      return value.currentState !== CheckboxStateEnum.Unchecked;
    }).map(([key]) => {
      return key;
    });
    return newOptions.map((key) => {
      const isChecked = selectedKeys.includes(key);
      const isIndeterminate = indeterminateKeys.includes(key);
      return (
        <li className="qes-multiselect__check-item" key={key}>
          <QESCheckbox
            label={props.options[key].displayValue}
            testId={`selected-${key}`}
            prefix={props.prefix}
            onChange={() => handleCheckboxClick(key)}
            indeterminate={isIndeterminate}
            checked={isChecked}
          />
        </li>
      )
    });
  }, [props.options]);

  return (
    <div
      className={cx({
        "error": fieldState === FormFieldStates.Error,
        "qes-multiselect__outer-container": true,
        [props.containerClassName]: !!props.containerClassName,
      })}
      key={props.testId}
      data-testid={props.testId}
      onBlur={() => setHasBeenFocused(true)}
    >
      <span className={`qes-text-input__label${props.hideLabel ? " visually-hidden-label" : ""}`}>
        {props.label}{props.required ? <span className="text-danger"> *</span> : ""}
        {props.validationTooltipContent ? <TooltipWrapper containerClassName="ml-3" text={props.validationTooltipContent}><i className="fa fa-question-circle" /></TooltipWrapper> : ""}
      </span>
      <div className="qes-multiselect__inner-container">
        {/* Using a fieldset here because setting `disabled` on it disables all form element children */}
        <fieldset className="qes-multiselect__checklist-container" disabled={props.disabled}>
          <QESTextInput label="Filter" testId={`multiselect-filter--${props.testId}`} onChange={(e) => setFilterString(e.target.value)} value={filterString} />
          <ul className="qes-multiselect__checklist">
            {renderedOptions}
          </ul>
        </fieldset>
        <fieldset className="qes-multiselect__selected-container" disabled={props.disabled}>
          <ul className="qes-multiselect__checklist">
            {renderedSelectedOptions}
          </ul>
        </fieldset>
      </div>
      {subText && <small className="qes-multiselect__descriptive-text">{subText}</small>}
    </div>
  );
};
